
import Vue from 'vue';
import { Component, Prop } from 'vue-property-decorator';
import DragHandle from './DragHandle';
import ElementGeneral from './ElementGeneral';
import draggable from 'vuedraggable';
import { DateTimeValueFormatUnixTimestampSeconds, VfbElement, VfbElementType } from './Element';

@Component({
  components: {
    draggable,
    DragHandle,
    ElementGeneral,
  },
})
export default class ElementObjectExtra extends Vue {
  @Prop({ required: true })
  builderElement!: VfbElement;
  @Prop({ default: false })
  isRoot!: boolean;

  expanded: string[] = [];
  newType: VfbElementType = 'text';
  newName = '';
  newLabel = '';

  get addText(): string {
    return this.builderElement.addText ?? '';
  }

  set addText(value: string) {
    if (typeof this.builderElement.addText === 'undefined') {
      this.$set(this.builderElement, 'addText', value); // if property didn't exist, it needs to be set this way, so vue makes it reactive
    }
    this.builderElement.addText = value;
  }

  get canAdd(): boolean {
    return !!this.newNameOrDefault && !this.usedNames.has(this.newNameOrDefault);
  }

  get elements(): VfbElement[] {
    return this.builderElement.schema ?? [];
  }

  set elements(value: VfbElement[]) {
    this.builderElement.schema = value;
  }

  get newNameOrDefault(): string {
    return this.newName || (this.newType + '_' + Date.now()).toString();
  }

  get typeOptions(): { value: VfbElementType, label: string }[] {
    return [
      { value: 'text', label: this.$t('workflow.vueFormBuilder.elements.general.types.text').toString() },
      { value: 'textarea', label: this.$t('workflow.vueFormBuilder.elements.general.types.textarea').toString() },
      { value: 'date', label: this.$t('workflow.vueFormBuilder.elements.general.types.date').toString() },
      { value: 'static', label: this.$t('workflow.vueFormBuilder.elements.general.types.static').toString() },
      { value: 'list', label: this.$t('workflow.vueFormBuilder.elements.general.types.list').toString() },
      { value: 'file', label: this.$t('workflow.vueFormBuilder.elements.general.types.file').toString() },
      { value: 'signature', label: this.$t('workflow.vueFormBuilder.elements.general.types.signature').toString() },
    ];
  }

  get usedNames(): Set<string> {
    return new Set(this.elements.map(e => e.name));
  }

  addField() {
    if (!this.canAdd) {
      return;
    }

    const element: VfbElement = {
      name: this.newNameOrDefault,
      type: this.newType,
      label: this.newLabel,
    };
    if (this.newType === 'date') {
      element.displayFormat = this.$t('workflow.vueFormBuilder.elements.date.displayFormatDefault').toString();
      element.valueFormat = DateTimeValueFormatUnixTimestampSeconds;
    }
    if (this.newType === 'list') {
      element.element = { type: 'object', name: 0, schema: [] };
      element.addText = this.$t('workflow.vueFormBuilder.elements.list.addTextDefault').toString();
    }
    if (this.newType === 'static') {
      element.tag = 'span';
    }

    this.elements.push(element);
    this.expanded.push(element.name);

    // reset inputs
    this.newType = 'text';
    this.newName = '';
    this.newLabel = '';

    // WORKAROUND: forces recalculation of the canAdd computed getter
    // needed so multiple elements without an entered name (name will be <type>_<timestamp>) can be added one after the other
    // otherwise add button will stay disabled because the newNameOrDefault computed getter will return the exact same name 
    // if everything happens in the same millisecond
    this.$nextTick(() => {
      this.newName = ' ';
      this.newName = '';
    });
  }

  removeField(name: string) {
    const index = this.elements.findIndex(e => e.name === name);
    if (index >= 0) {
      this.elements.splice(index, 1);
    }
    if (this.expanded.includes(name)) {
      this.expanded.splice(this.expanded.indexOf(name), 1);
    }
  }

  toggleCollapse(name: string) {
    if (this.expanded.includes(name)) {
      this.expanded.splice(this.expanded.indexOf(name), 1);
    } else {
      this.expanded.push(name);
    }
  }
}
